import { useState } from "react";
import { useProvider } from "../lib/providersManifest";

/**
 * Iter-185.J/K — CarrierLogo with premium initials-chip fallback.
 * Iter-185.L — Pulls the canonical logo URL from the DB-driven providers
 *              manifest (admin-editable) and falls back to the
 *              filesystem-based `/logos/logo-{slug}.{svg|png}` path when
 *              the slug isn't catalogued yet. Net result: the owner can
 *              upload a new logo from the admin panel without any code
 *              edits — the table picks it up on the next manifest
 *              refresh.
 * Iter-185.V — Added `uniform` size variant + cleaner default sizing
 *              for mobile tables per user feedback ("audit all tables
 *              for logo size everything should resize to fit tables
 *              uniform. Currently on mobile the logos look off please
 *              fix so when I provide logos it all fits seamlessly").
 *              The `uniform` variant constrains every logo into a
 *              FIXED-width letterbox cell (w-[100px] mobile,
 *              w-[140px] desktop) with object-contain inside — so
 *              wide logos (Progressive 8.4:1) and square logos
 *              (Allstate 1.45:1) render with the SAME footprint.
 *
 * Resolution order:
 *   1. `logoUrl` prop (callers can hard-pin a path).
 *   2. DB-driven provider.logo_url from the manifest.
 *   3. Filesystem path `/logos/logo-{slug}.svg` then `.png`.
 *   4. Premium initials chip fallback (slate-100 + initials + name).
 *
 * Props:
 *  - slug    : carrier slug (e.g. "progressive-insurance"). Used to
 *              look up the provider record in the manifest.
 *  - name    : human-readable carrier name (used as <img alt> +
 *              fallback initials).
 *  - logoUrl : optional override — short-circuits the manifest + FS
 *              lookup and renders this URL directly.
 *  - size    : "sm" (mobile rows) | "md" (desktop rows) |
 *              "uniform" (iter-185.V — fixed-width letterbox cell so
 *              wide + square logos render at the same footprint).
 *              Default "md".
 *  - className : extra wrapper classes for layout overrides.
 */
export default function CarrierLogo({ slug, name, logoUrl, size = "md", className = "" }) {
  // Iter-185.L — Consume DB manifest. Returns null while loading or
  // when the slug isn't catalogued; component falls back to the
  // filesystem path in that case.
  const provider = useProvider(slug);
  const dbLogoUrl = provider?.logo_url;
  const dbAlt = provider?.logo_alt;

  // Sources to try, in order:
  //   1. explicit prop override
  //   2. DB-managed URL
  //   3. filesystem .svg
  //   4. filesystem .png
  const sources = [
    logoUrl || null,
    dbLogoUrl || null,
    `/logos/logo-${slug}.svg`,
    `/logos/logo-${slug}.png`,
  ].filter(Boolean);

  const [attempt, setAttempt] = useState(0);

  // Iter-185.V/W/BP — Sizing strategy by variant:
  //   "uniform" — fixed letterbox cell with `object-contain` so wide
  //     logos (Progressive 8.4:1) and square logos (Allstate 1.45:1)
  //     render with the same footprint.
  //   Iter-185.BP normalized the aspect ratio to 2.5:1 (was 3.18:1) so
  //     square-ish brand marks (Allstate, USAA, AAA) fill noticeably
  //     more of the cell. Wide logos (Progressive, LightStream) are
  //     still capped by max-w-full so they don't overflow.
  //   The img uses `w-full h-full object-contain` (not max-*) so SVGs
  //     are UPSCALED to fill the box up to their aspect ratio, then
  //     centered. This visibly equalizes the square-vs-wide footprint.
  //   "md" — height-driven sizing with a min-width floor.
  //   "sm" — compact height-driven sizing for mobile-dense rows.
  let cellClasses;
  if (size === "uniform") {
    // 110×44 = 2.5:1. Compromise between square (1.45:1) and
    // wide (8.4:1) brand marks. Empirically tested with the 14
    // iter-185.BN carriers — all render at visually consistent size.
    cellClasses = "h-11 w-[110px] md:w-[120px]";
  } else if (size === "md") {
    cellClasses = "h-10 md:h-12 min-w-[120px] md:min-w-[150px]";
  } else {
    cellClasses = "h-8 md:h-9 min-w-[88px] md:min-w-[110px]";
  }

  if (attempt >= sources.length) {
    // Premium initials chip instead of plain-text fallback.
    const initials = extractInitials(name);
    const chipSize =
      size === "uniform"
        ? "h-11 w-11"
        : size === "md"
        ? "h-10 md:h-12 w-12 md:w-14"
        : "h-8 md:h-9 w-9 md:w-10";
    return (
      <div
        data-testid={`carrier-logo-fallback-${slug}`}
        className={`inline-flex items-center gap-2.5 ${className}`}
      >
        <span
          className={`${chipSize} inline-flex items-center justify-center bg-slate-100 border border-slate-200 text-slate-700 font-display font-black text-sm md:text-base tracking-tight`}
          aria-hidden
        >
          {initials}
        </span>
        <span
          data-testid={`carrier-name-${slug}`}
          className="font-display font-black text-sm md:text-base text-slate-950 leading-tight"
        >
          {name}
        </span>
      </div>
    );
  }

  // Iter-185.W/BP — `uniform` variant wraps the <img> in a fixed-size
  // letterbox cell. The inner img uses `w-full h-full object-contain`
  // (no max-* constraints) so SVGs are UPSCALED to fill the box up to
  // their aspect ratio. Combined with the 2.5:1 letterbox aspect
  // (iter-185.BP), square + wide logos render at visually consistent
  // size.
  if (size === "uniform") {
    return (
      <div
        data-testid={`carrier-logo-cell-${slug}`}
        className={`${cellClasses} inline-flex items-center justify-center overflow-hidden ${className}`}
      >
        <img
          data-testid={`carrier-logo-${slug}`}
          data-source={dbLogoUrl && sources[attempt] === dbLogoUrl ? "db" : "fs"}
          data-size={size}
          src={sources[attempt]}
          alt={dbAlt || name}
          loading="lazy"
          decoding="async"
          onError={() => setAttempt((a) => a + 1)}
          className="w-full h-full object-contain object-center block"
        />
      </div>
    );
  }

  // sm/md legacy variants — height-driven with min-width floor and
  // left-anchored alignment alongside table-cell text.
  const imgMaxW = "max-w-[200px]";

  return (
    <img
      data-testid={`carrier-logo-${slug}`}
      data-source={dbLogoUrl && sources[attempt] === dbLogoUrl ? "db" : "fs"}
      data-size={size}
      src={sources[attempt]}
      alt={dbAlt || name}
      loading="lazy"
      decoding="async"
      onError={() => setAttempt((a) => a + 1)}
      className={`${cellClasses} w-auto ${imgMaxW} object-contain object-left block ${className}`}
    />
  );
}

// Pull 1-2 alphanumeric initials from a carrier name. Strips
// punctuation, marketplace suffixes, etc.
function extractInitials(name) {
  if (!name) return "?";
  const cleaned = name
    .replace(/[^a-zA-Z0-9\s]/g, "")
    .replace(/\b(insurance|inc|llc|company|co|corp)\b/gi, "")
    .trim();
  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
